/* eslint-disable no-control-regex */
import * as Yup from 'yup';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInYears from 'date-fns/differenceInYears';
import valid from 'card-validator';

export const email = Yup.string().email('Invalid email address').required('Required');

export const firstName = Yup.string()
  .min(2, 'Minimum length is 2')
  .max(25, 'Maximum length is 25')
  .test('test-first-name', 'Name containing numbers is not valid', (value) =>
    /^([^0-9]*)$/g.test(value)
  )
  .test('isExtendedASCII', 'Name contains invalid characters', (value) =>
    /^([\x00-\xFF]*)$/g.test(value)
  )
  .required('Required');

export const lastName = Yup.string()
  .min(2, 'Minimum length is 2')
  .max(35, 'Maximum length is 35')
  .test('test-last-name', 'Name containing numbers is not valid', (value) =>
    /^([^0-9]*)$/g.test(value)
  )
  .test('isExtendedASCII', 'Name contains invalid characters', (value) =>
    /^([\x00-\xFF]*)$/g.test(value)
  )
  .required('Required');

export const phoneCountryCode = Yup.string()
  .required('Required')
  .max(4, 'Maximum country code length is 4');

export const phoneNumber = Yup.string()
  .required('Required')
  .min(3, 'Minimum phone number length is 3')
  .when('phoneCountryCode', (phoneCountryCode, schema) => {
    return schema.max(
      phoneCountryCode ? 16 - phoneCountryCode.length : 14,
      'Phone number is too long'
    );
  });

export const phoneValidationSchema = Yup.object({
  phoneCountryCode,
  phoneNumber
});

export const dateOfBirth = Yup.mixed()
  .test('isDateValid', 'Required', (inputDate) => {
    const date = parse(inputDate, 'yyyy-MM-dd', new Date());
    return isValid(date);
  })
  .test(
    'isPersonOlder',
    'Currently we can offer insurance only for customers that are younger than 70 years old',
    (inputDate) => {
      const date = parse(inputDate, 'yyyy-MM-dd', new Date());
      const difference = differenceInYears(new Date(), date);
      return difference < 70;
    }
  )
  .test(
    'isPersonOlder',
    'Currently we can offer insurance only for customers that are older than 14 days',
    (inputDate) => {
      const date = parse(inputDate, 'yyyy-MM-dd', new Date());
      const difference = differenceInDays(new Date(), date);
      return difference > 14;
    }
  );

export const dateOfBirthWithMinorCheck = dateOfBirth.test(
  'isPersonOlder',
  'You can’t buy insurance for yourself if you are under 18 years old. Please contact customer service.',
  (inputDate) => {
    const date = parse(inputDate, 'yyyy-MM-dd', new Date());
    const difference = differenceInYears(new Date(), date);
    return difference >= 18;
  }
);

export const gender = Yup.string()
  .oneOf(['male', 'female'], 'Biological sex is required')
  .required('Required');

export const address = Yup.object().shape({
  country: Yup.string().required('Required'),
  city: Yup.string()
    .min(2, 'Minimum length is 2')
    .max(30, 'Maximum length is 30')
    .test('isExtendedASCII', 'City contains invalid characters', (value) =>
      /^([\x00-\xFF]*)$/g.test(value)
    )
    .required('Required'),
  line1: Yup.string()
    .min(2, 'Minimum length is 2')
    .max(55, 'Maximum length is 55')
    .test('isExtendedASCII', 'Address contains invalid characters', (value) =>
      /^([\x00-\xFF]*)$/g.test(value)
    )
    .required('Required'),
  zipCode: Yup.mixed().when('country', {
    is: 'US',
    then: Yup.string()
      .test('Numbers only', 'Numbers only', (value) => /^\d+$/.test(value))
      .min(5, 'Minimum length is 5')
      .max(10, 'Maximum length is 10')
      .required('Required'),
    otherwise: Yup.string()
      .min(2, 'Minimum length is 2')
      .max(10, 'Maximum length is 10')
      .test('isExtendedASCII', 'Zip contains invalid characters', (value) =>
        /^([\x00-\xFF]*)$/g.test(value)
      )
      .required('Required')
  }),
  state: Yup.mixed().when('country', {
    is: 'US',
    then: Yup.string()
      .min(2, 'Minimum length is 2')
      .test('isExtendedASCII', 'State contains invalid characters', (value) =>
        /^([\x00-\xFF]*)$/g.test(value)
      )
      .required('Required')
  })
});

export const stateRequiredForUsAndUa = Yup.mixed()
  .when('country', {
    is: 'US',
    then: Yup.string()
      .min(2, 'Minimum length is 2')
      .test('isExtendedASCII', 'State contains invalid characters', (value) =>
        /^([\x00-\xFF]*)$/g.test(value)
      )
      .required('Required')
  })
  .when('country', {
    is: 'UA',
    then: Yup.string()
      .min(2, 'Minimum length is 2')
      .test('isExtendedASCII', 'State contains invalid characters', (value) =>
        /^([\x00-\xFF]*)$/g.test(value)
      )
      .required('Required')
  });

export const addressWithStateRequiredForUsAndUa = address.shape({
  state: stateRequiredForUsAndUa
});

export const creditCardHolderName = Yup.string()
  .test('test-name', 'Invalid', (value) => valid.cardholderName(value).isValid)
  .test('isExtendedASCII', 'Invalid', (value) => /^([\x00-\xFF]*)$/g.test(value))
  .min(2, 'Minimum length is 2')
  .max(60, 'Maximum length is 60')
  .test('card-holder-name', 'Name containing numbers is not valid', (value) =>
    /^([^0-9]*)$/g.test(value)
  )
  .required();

export const creditCardCCV = Yup.string()
  .test('test-cvv', 'Invalid', function (value) {
    const { creditCardType } = this.parent;
    return valid.cvv(value, creditCardType === 'AMEX' ? 4 : 3).isValid;
  })
  .required();

export const creditCardNumber = Yup.string()
  .test('test-number', 'Invalid', (value) => valid.number(value).isValid)
  .required();

export const nonStripePaymentValidationSchema = Yup.object({
  creditCardCCV,
  creditCardExpiryMonth: Yup.string()
    .test('test-month', 'Invalid', (value) => valid.expirationMonth(value).isValid)
    .required(),
  creditCardExpiryYear: Yup.string()
    .test('test-year', 'Invalid', (value) => valid.expirationYear(value).isValid)
    .required(),
  creditCardNumber,
  creditCardHolderName,
  creditCardType: Yup.string().required('Required').nullable()
});
